/** @file Per-layer pivot hints (feedback B3): set, edit or clear a pivot in each rigged layer's pixels. */
import type { Translate } from '@pose2d/i18n/types';
import { Button } from '../../ui-kit/Button';
import { NumberField } from '../../ui-kit/NumberField';
import type { Pivots } from './autorigTypes';
import type { PlanPart } from './planParts';

type Pivot = readonly [number, number];

function PivotRow({ t, part, pivot, onSet }: { t: Translate; part: PlanPart; pivot: Pivot | undefined; onSet: (p: Pivot | undefined) => void }) {
  return (
    <div className="autorig-pivot">
      <svg className="autorig-pivot-box" viewBox={`0 0 ${String(part.w)} ${String(part.h)}`} aria-hidden="true">
        <rect width={part.w} height={part.h} />
        {pivot === undefined ? null : <circle cx={pivot[0]} cy={pivot[1]} r={Math.max(part.w, part.h) / 10} />}
      </svg>
      <span>{part.name}</span>
      {pivot === undefined ? <span className="autorig-note">{t('autorig.pivotAuto')}</span> : (
        <>
          <NumberField label={t('autorig.pivotX')} value={pivot[0]} min={0} max={part.w} precision={0} onCommit={(x) => { onSet([x, pivot[1]]); }} />
          <NumberField label={t('autorig.pivotY')} value={pivot[1]} min={0} max={part.h} precision={0} onCommit={(y) => { onSet([pivot[0], y]); }} />
        </>
      )}
      {pivot === undefined
        ? <Button label={t('autorig.pivotSet')} onClick={() => { onSet([Math.round(part.w / 2), Math.round(part.h / 2)]); }} />
        : <Button label={t('autorig.pivotClear')} onClick={() => { onSet(undefined); }} />}
    </div>
  );
}

/** Props of PivotHints; pivots are keyed by image name, in that image's pixels (top-left origin, y down). */
export interface PivotHintsProps {
  t: Translate;
  parts: readonly PlanPart[];
  pivots: Pivots;
  onChange: (next: Pivots) => void;
}

/** A new hint starts at the layer's centre and is clamped to the layer; nothing renders without parts. */
export function PivotHints({ t, parts, pivots, onChange }: PivotHintsProps) {
  if (parts.length === 0) return null;
  const set = (name: string, pivot: Pivot | undefined): void => {
    const rest = Object.entries(pivots).filter(([key]) => key !== name);
    onChange(Object.fromEntries(pivot === undefined ? rest : [...rest, [name, pivot]]));
  };
  return (
    <fieldset className="autorig-pivots">
      <legend>{t('autorig.pivots')}</legend>
      {parts.map((part) => <PivotRow key={part.name} t={t} part={part} pivot={pivots[part.name]} onSet={(p) => { set(part.name, p); }} />)}
    </fieldset>
  );
}
